import { useMutation, useQuery } from "@tanstack/react-query"
import {
  exportMagicLinkReport,
  exportSchoolReport,
  generateMagicLinkReport,
  generateSchoolReport,
  getSchoolBatches,
  getSchoolStudents,
} from "@/services/geniusService"
import { getSchoolDetails } from "@/services/schoolsService"
import { generateMagicLinkReportPayload, generateSchoolReportPayload, toSchoolBatches, toSchoolDetails, toSchoolStudents } from "@/types/school"
import { downloadFileFromUrl, getFileNameFromUrl } from "@/lib/downloadFile"
import { extractErrorMessage, type ApiErrorPayload } from "@/api/extractErrorMessage"
import { getSchoolDetailsPublic } from "@/services/schoolReportService"

const getApiErrorMessage = (error: unknown): string => {
  if (!error) return ''

  if (typeof error !== 'object' || error === null) {
    return String(error)
  }

  const axiosError = error as { response?: { data?: ApiErrorPayload }; message?: string }
  return extractErrorMessage(axiosError.response?.data, axiosError.message ?? '')
}

export const useSchoolBatches = (schoolId: string, enabled : boolean) => {
    return useQuery({
        queryKey: ['school-batches', schoolId],
        queryFn: async () => toSchoolBatches(await getSchoolBatches(schoolId)),
        enabled: enabled && schoolId.trim().length > 0,
        staleTime: 0,
        retry: 1,
    })
}

export const useSchoolDetails = (schoolId: string) => {
    return useQuery({
        queryKey: ['school-details', schoolId],
        queryFn: async () => {
            const { data } = await getSchoolDetails(schoolId.toUpperCase());
            const school = data?.schools?.[0];
            if (!school) {
                throw new Error('No school found for the given School ID');
            }
            return toSchoolDetails(school);
        },
        enabled: schoolId.trim().length > 0,
        staleTime: 0,
    })
}

export const useSchoolDetailsPublic = (schoolId: string) => {
  return useQuery({
    queryKey: ["school-details", schoolId],
    queryFn: async () => {
      const { data } = await getSchoolDetailsPublic(schoolId.toUpperCase());
      const school = data?.schools?.[0];
      if (!school) {
        throw new Error("No school found for the given School ID");
      }
      return toSchoolDetails(school);
    },
    enabled: schoolId.trim().length > 0,
    staleTime: 0,
  });
};

export const useSchoolStudents = (
    schoolId: string,
    enabled: boolean,
    params?: { batch_ids?: string[]; exam_date?: string }
) => {
    const batchIds = params?.batch_ids?.length ? params.batch_ids : undefined;
    const examDate = params?.exam_date || undefined;

    return useQuery({
        queryKey: ['school-students', schoolId, batchIds, examDate],
        queryFn: async () => toSchoolStudents(
            await getSchoolStudents(schoolId, { batch_ids: batchIds!, exam_date: examDate })
        ),
        // Students API requires batch_ids — calling without them returns 422
        enabled: enabled && schoolId.trim().length > 0 && !!batchIds?.length,
        retry: 1,
        staleTime: 0,
    })
}

export const useExportMagicLinkReport = () => {
    return useMutation({
        mutationFn: (schoolId: string) => exportMagicLinkReport(schoolId),
        onSuccess: async ({ file_url }) => {
            await downloadFileFromUrl(file_url, getFileNameFromUrl(file_url, 'magic-link-report'));
        },
    })
}

export const useExportSchoolReport = () => {
    return useMutation({
        mutationFn: (schoolId: string) => exportSchoolReport(schoolId),
        onSuccess: async ({ file_url }) => {
            await downloadFileFromUrl(file_url, getFileNameFromUrl(file_url, 'school-report'));
        },
    })
}

export const useGenerateMagicLinkReport = () => {
    const mutation = useMutation<unknown, unknown, { schoolId: string; payload: generateMagicLinkReportPayload }>({
        mutationFn: ({ schoolId, payload }) => generateMagicLinkReport(schoolId, payload),
    })

    return {
        ...mutation,
        errorDescription: getApiErrorMessage(mutation.error),
    }
}

export const useGenerateSchoolReport = () => {
    const mutation = useMutation<unknown, unknown, { schoolId: string; payload: generateSchoolReportPayload }>({
        mutationFn: ({ schoolId, payload }) => generateSchoolReport(schoolId, payload),
    })

    return {
        ...mutation,
        errorDescription: getApiErrorMessage(mutation.error),
    }
}
